/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionClearFilters';

const ActionClearFiltersManager: ActionManagerFn<'ActionClearFilters'> = (
  platform: Platform,
): ActionManager<'ActionClearFilters'> => {
  return {
    ...baseActionManager(platform, 'ActionClearFilters'),
    defaultValue,
    props: {
      ...baseActionProps,
      alias: {
        label: 'Datasource Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
        ],
      },
      clearSmartSearchFilters: {
        label: 'Clear Smartsearch Filters',
        type: 'boolean',
      },
      clearGridFilters: {
        label: 'Clear Grid Filters',
        type: 'boolean',
      },
      clearStoreFilters: {
        label: 'Clear Store Filters',
        type: 'boolean',
      },
    },
    name: 'Clear Filters',
    getDisplayText: (item) => item.props.alias,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    icon: { iconName: 'eraser' },
    description: `**Overview**<br/>
The Clear Filters action is a state management component designed to reset the filtering criteria applied to a specific Data Store. It provides a mechanism to selectively remove different categories of filters—such as those generated by smart searches, grid interactions, or explicit query actions—restoring the data store to a broader or default view state.<br/><br/>
This component operates by interacting directly with the Data Store's internal state. It offers granular control through boolean toggles, allowing developers to decide exactly which types of filters should be purged while leaving others intact, or to perform a comprehensive cleanup of all active constraints.

**Key Features**<br/>
* **Granular Filter Management:** Distinct boolean properties allow for the independent clearing of Smart Search filters, Grid-based filters, and general Store filters, providing precise control over the data view.
* **Broad Data Store Compatibility:** Supports interaction with multiple data store types, including CloudDataStore, DataStore, LocalDataStore, and ReadOnlyDataStore.
* **Dependency Tracking:** Implements the isDataSourceAliasUsed method to programmatically report usage of specific data source aliases, aiding in system-wide dependency analysis and maintenance.

**Properties**<br/>
* **Datasource Alias (Required):** A DataSourceAliasChooser property that identifies the target Data Store from which filters will be removed.
* **Clear Smartsearch Filters:** A boolean toggle to specifically remove filters applied via Smart Search components.
* **Clear Grid Filters:** A boolean toggle to remove filters applied directly through Grid UI interactions (e.g., column filtering).
* **Clear Store Filters:** A boolean toggle to remove general filters applied via standard Query actions or store logic.

**Use Cases**<br/>
* **Search Reset:** Providing a "Reset" button functionality that clears user-defined search terms (Smart Search) without affecting structural grid filters.
* **View Restoration:** Removing all user-imposed constraints (Grid and Store filters) to return a dashboard or list to its initial default state.`,
  };
};

export default ActionClearFiltersManager;
